// 纯展示转换：公开事件流水，以及已授权能力状态的文字／样式。
// 不推断角色真值、能力合法性或卡牌条件；输入只来自当前视角的投影。

import { CARD_COPY, MODE_LABEL, cardEffect, roleCopy } from "./glossary.ts";
import type { AbilityState, GameEvent, Room, State } from "./types.ts";

export function abilityStateText(ability: AbilityState, actionable = false): string {
  const details = [actionable || ability.usable ? "可用" : "待機"];
  if (ability.uses !== undefined) details.push(`已发动 ${ability.uses} 次`);
  else if (ability.used) details.push("曾发动");
  if (ability.progress !== undefined || ability.id === "marisa_master_spark") {
    details.push(`${ability.id === "marisa_master_spark" ? "累计伤害" : "累计值"} ${ability.progress ?? 0}`);
  }
  return details.join(" · ");
}

export function abilityStateClass(ability: AbilityState, actionable = false): string {
  return actionable || ability.usable ? "is-usable" : ability.used ? "is-used" : "";
}

export interface TextContext {
  state: State | null;
  room: Room | null;
  cards: Record<string, string>;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
}

function textOf(data: Record<string, unknown>, key: string): string {
  const value = data[key];
  return typeof value === "string" ? value : "";
}

function intOf(data: Record<string, unknown>, key: string): number {
  const value = data[key];
  return typeof value === "number" ? value : 0;
}

function seatName(ctx: TextContext, id: string): string {
  if (!id) return "？";
  const fromState = ctx.state?.players.find((player) => player.id === id);
  if (fromState) return fromState.name;
  const fromRoom = ctx.room?.players.find((player) => player.player_id === id);
  return fromRoom?.name ?? "未知玩家";
}

function placeName(ctx: TextContext, slot: number): string {
  const place = ctx.state?.board.places.find((entry) => entry.slot === slot);
  return place ? place.name : "未知场所";
}

function cardLabel(ctx: TextContext, data: Record<string, unknown>): string {
  const brief = asRecord(data["card"]);
  const id = textOf(brief, "id") || textOf(data, "card_id");
  if (!id) return "？";
  const observed = textOf(brief, "text") || ctx.cards[id];
  const copy = CARD_COPY[id];
  if (copy) return `${copy.name}：${cardEffect(id, observed)}`;
  return observed ?? "未识别卡牌";
}

/** CAUSE_LABEL 是事件里 damage/heal 的 cause 取值。 */
const CAUSE_LABEL: Record<string, string> = {
  card: "卡牌",
  attack: "攻击",
  place: "场所",
  ability: "能力",
  klein: "命莲寺卡",
  judge: "判定",
  setup: "开局",
  loot: "夺取",
};

export function eventText(event: GameEvent, ctx: TextContext): string {
  const data = asRecord(event.data);
  const who = seatName(ctx, textOf(data, "player"));
  switch (event.type) {
    case "game_created": {
      const mode = textOf(data, "mode");
      const seed = intOf(data, "seed");
      return `对局建立${mode ? `（${MODE_LABEL[mode] ?? "規則不明"}）` : ""}${
        seed ? `，种子 ${seed}` : ""
      }`;
    }
    case "game_started":
      return "对局开始：各就各位";
    case "turn_started":
      return `第 ${intOf(data, "round")} 回合 —— ${who} 的回合`;
    case "turn_ended":
      return `${who} 结束回合`;
    case "dice_rolled": {
      const dice = [intOf(data, "d4"), intOf(data, "d6")].filter((value) => value > 0);
      const cause = textOf(data, "cause");
      const label = cause === "move" ? "移动" : CAUSE_LABEL[cause] ?? "判定";
      return `${who} ${label}掷骰：${dice.join("+")}=${intOf(data, "sum")}`;
    }
    case "move": {
      const d4 = intOf(data, "d4");
      const d6 = intOf(data, "d6");
      const sum = intOf(data, "sum");
      const to = placeName(ctx, intOf(data, "to_slot"));
      switch (textOf(data, "cause")) {
        case "setup":
          return `${who} 开局落位：${d4}+${d6}=${sum} → ${to}`;
        case "adjacent":
          return `${who} 相邻移动 → ${to}`;
        default:
          return `${who} 掷出 ${d4}+${d6}=${sum} → ${to}`;
      }
    }
    case "reroll":
      return `${who} 骰目落回原地，重掷`;
    case "card_drawn":
      return `${who} 翻开卡牌：${cardLabel(ctx, data)}`;
    case "card_resolved":
      return `${who} 结算卡牌：${cardLabel(ctx, data)}`;
    case "equipment_gained":
      return `${who} 得到装备：${cardLabel(ctx, data)}`;
    case "equipment_lost":
      return `${who} 失去装备：${cardLabel(ctx, data)}`;
    case "klein_drawn":
      return `${who} 抽到命莲寺卡：${cardLabel(ctx, data)}`;
    case "klein_given":
      return `${who} 把命莲寺卡交给 ${seatName(ctx, textOf(data, "target"))}`;
    case "klein_resolved":
      return `${who} ${
        textOf(data, "direction") === "obey" ? "遵从指示" : "宣言「什么都不会发生」"
      }（${cardLabel(ctx, data)}）`;
    case "damage": {
      const cause = CAUSE_LABEL[textOf(data, "cause")] ?? "未知";
      const from = textOf(data, "by");
      return `${who} 受到 ${intOf(data, "amount")} 点伤害（${cause}${
        from ? ` · ${seatName(ctx, from)}` : ""
      }），伤害值 ${intOf(data, "after")}`;
    }
    case "heal":
      return `${who} 回复 ${intOf(data, "amount")} 点伤害（${CAUSE_LABEL[textOf(data, "cause")] ?? "未知"}）`;
    case "revealed": {
      const role = asRecord(data["role"]);
      const id = textOf(role, "id");
      const name = roleCopy(id)?.name ?? textOf(role, "name");
      return `${who} 公开身份：${name || "潜藏的英灵"}`;
    }
    case "died":
      return `${who} 退场`;
    case "loot":
      return `${who} 夺取 ${seatName(ctx, textOf(data, "target"))} 的装备`;
    case "extra_turn":
      return `${who} 获得追加回合`;
    case "judge_check": {
      const die = intOf(data, "die");
      const face = intOf(data, "face");
      const verdict = textOf(data, "result");
      return `判定 ${who}：${die} 面骰掷出 ${face}${verdict ? ` → ${verdict}` : ""}`;
    }
    case "game_over": {
      const winners = Array.isArray(data["winners"]) ? data["winners"] : [];
      const names = winners
        .map((winner) => seatName(ctx, textOf(asRecord(winner), "player")))
        .join("、");
      return `对局结束：${names || "无人胜出"}`;
    }
    default:
      return "记录了一项未识别事件";
  }
}
